package co.maytes.standupbot.dashboard.features.standupdetail

import co.maytes.standupbot.dashboard.features.standups.StandupsStore
import co.maytes.standupbot.dashboard.features.standupruns.StandupRunsStore
import co.maytes.standupbot.dashboard.lib.api.Result
import com.kttipay.mvi.api.Container
import com.kttipay.mvi.api.StoreScope
import com.kttipay.mvi.dsl.store
import kotlinx.coroutines.CoroutineScope
import kotlinx.datetime.LocalDate

class StandupDetailContainer(
    scope: CoroutineScope,
    private val standupsStore: StandupsStore,
    private val runsStore: StandupRunsStore,
) : Container<StandupDetailContract.State, StandupDetailContract.Intent, StandupDetailContract.Action> {

    override val store = scope.store<
            StandupDetailContract.State,
            StandupDetailContract.Intent,
            StandupDetailContract.Action,
            >(initialState = StandupDetailContract.State()) {
        configure(name = "StandupDetail")
        logging()
        reduce { intent ->
            when (intent) {
                is StandupDetailContract.Intent.Load -> {
                    updateState {
                        copy(
                            workspaceId = intent.workspaceId,
                            standupId = intent.standupId,
                        )
                    }
                    load()
                }
                StandupDetailContract.Intent.Retry -> load(force = true)
                StandupDetailContract.Intent.Back ->
                    action(StandupDetailContract.Action.Navigate("/app/standups"))
                is StandupDetailContract.Intent.SelectTab -> updateState { copy(activeTab = intent.tab) }
                is StandupDetailContract.Intent.Trigger -> runTrigger(intent.date)
                StandupDetailContract.Intent.OpenRunForDate -> updateState { copy(runForDateOpen = true) }
                StandupDetailContract.Intent.CloseRunForDate -> updateState { copy(runForDateOpen = false) }
                StandupDetailContract.Intent.OpenDelete -> updateState { copy(deleteOpen = true) }
                StandupDetailContract.Intent.CloseDelete -> updateState { copy(deleteOpen = false) }
                StandupDetailContract.Intent.ConfirmDelete -> deleteStandup()
                is StandupDetailContract.Intent.Save -> save(intent.patch)
            }
        }
    }

    private suspend fun StoreScope<StandupDetailContract.State, StandupDetailContract.Action>.load(
        force: Boolean = false,
    ) {
        val state = currentState
        if (state.workspaceId.isBlank() || state.standupId.isBlank()) return

        val cached = standupsStore.cachedDetail(state.standupId)
        if (cached != null && !force) {
            updateState { copy(standup = cached, status = StandupDetailContract.Status.Ready) }
        } else {
            updateState { copy(status = StandupDetailContract.Status.Loading) }
            when (val res = standupsStore.detail(state.workspaceId, state.standupId)) {
                is Result.Ok ->
                    updateState {
                        copy(standup = res.value, status = StandupDetailContract.Status.Ready)
                    }
                is Result.Err ->
                    updateState { copy(status = StandupDetailContract.Status.Error(res.error)) }
            }
        }
        if (standupsStore.cachedMembers(state.standupId) == null) {
            standupsStore.members(state.workspaceId, state.standupId)
        }
        loadRecentRuns()
    }

    private suspend fun StoreScope<StandupDetailContract.State, StandupDetailContract.Action>.loadRecentRuns() {
        val state = currentState
        if (state.workspaceId.isBlank() || state.standupId.isBlank()) return
        val res = runsStore.listByStandup(state.workspaceId, state.standupId, limit = 5)
        if (res is Result.Ok) {
            updateState { copy(recentRuns = res.value) }
        }
    }

    private suspend fun StoreScope<StandupDetailContract.State, StandupDetailContract.Action>.runTrigger(
        date: LocalDate?,
    ) {
        val state = currentState
        val standup = state.standup ?: return
        if (state.triggerInFlight) return

        updateState { copy(triggerInFlight = true, runForDateOpen = false) }
        val res = standupsStore.trigger(state.workspaceId, standup.id, date)
        updateState { copy(triggerInFlight = false) }
        when (res) {
            is Result.Ok -> {
                val id = res.value.standupId
                if (id.isNotBlank() && id != "pending") {
                    action(StandupDetailContract.Action.Navigate("/app/runs/$id"))
                } else {
                    val suffix = date?.let { " for $it" }.orEmpty()
                    action(
                        StandupDetailContract.Action.Toast(
                            "Queued$suffix — posting to #${standup.slackChannelName ?: standup.slackChannelId}",
                        ),
                    )
                }
            }
            is Result.Err ->
                action(
                    StandupDetailContract.Action.Toast(
                        "Run failed: ${res.error.displayMessage}",
                        error = true,
                    ),
                )
        }
    }

    private suspend fun StoreScope<StandupDetailContract.State, StandupDetailContract.Action>.deleteStandup() {
        val state = currentState
        val standup = state.standup ?: return
        when (val res = standupsStore.delete(state.workspaceId, standup.id)) {
            is Result.Ok -> {
                action(StandupDetailContract.Action.Toast("Standup deleted"))
                action(StandupDetailContract.Action.Navigate("/app/standups"))
            }
            is Result.Err -> {
                updateState { copy(deleteOpen = false) }
                action(
                    StandupDetailContract.Action.Toast(
                        "Delete failed: ${res.error.displayMessage}",
                        error = true,
                    ),
                )
            }
        }
    }

    private suspend fun StoreScope<StandupDetailContract.State, StandupDetailContract.Action>.save(
        patch: co.maytes.standupbot.shared.dto.standup.UpdateStandupRequestDto,
    ) {
        val state = currentState
        val standup = state.standup ?: return
        if (state.saveInFlight) return

        updateState { copy(saveInFlight = true) }
        val res = standupsStore.update(state.workspaceId, standup.id, patch)
        updateState { copy(saveInFlight = false) }
        when (res) {
            is Result.Ok -> {
                updateState { copy(standup = res.value) }
                action(StandupDetailContract.Action.Toast("Saved"))
            }
            is Result.Err ->
                action(
                    StandupDetailContract.Action.Toast(
                        "Save failed: ${res.error.displayMessage}",
                        error = true,
                    ),
                )
        }
    }
}
